<template>
    <div class="invoice-page">
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Subscription Invoice</h5>
          <p class="page-sub">View and download your subscription payment receipt</p>
        </div>
        <div class="page-actions">
          <router-link :to="$url('/subscription-plan')" class="btn-ghost">
            <font-awesome-icon icon="angle-left" /> Back
          </router-link>
          <button class="btn-outline" @click="printInvoice" :disabled="loading">
            <font-awesome-icon icon="print" /> Print
          </button>
          <button class="btn-primary" @click="createPDF('invoice-content')" :disabled="loading">
            <font-awesome-icon icon="download" /> Download PDF
          </button>
        </div>
      </div>
  
      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading invoice...</p>
      </div>
  
      <!-- Invoice card -->
      <div v-else class="invoice-card" id="invoice-content">
  
        <!-- Invoice header -->
        <div class="invoice-head">
          <div class="invoice-brand">
            <div class="brand-logo" v-if="details?.logo">
              <img :src="url.hostName + 'images/logo/' + details.logo" alt="Logo" class="logo-img" />
            </div>
            <div v-else class="brand-name">{{ details?.company_name }}</div>
          </div>
          <div class="invoice-meta">
            <div class="invoice-label">INVOICE</div>
            <div class="invoice-number">{{ subscriptions?.invoice_number }}</div>
            <div class="invoice-status status--paid">
              <span class="status-dot"></span> Paid
            </div>
          </div>
        </div>
  
        <div class="invoice-divider"></div>
  
        <!-- Addresses -->
        <div class="invoice-addresses">
  
          <div class="address-block">
            <div class="address-label">From</div>
            <div class="address-company">{{ details?.company_name }}</div>
            <div class="address-detail">{{ details?.address }}</div>
            <div class="address-detail" v-if="details?.email">
              <font-awesome-icon icon="envelope" class="me-1" />
              {{ details?.email }}
            </div>
            <div class="address-detail" v-if="details?.tel">
              <font-awesome-icon icon="phone" class="me-1" />
              {{ details?.tel }}
            </div>
            <div class="address-detail" v-if="details?.tel2">
              <font-awesome-icon icon="phone" class="me-1" />
              {{ details?.tel2 }}
            </div>
          </div>
  
          <div class="address-block">
            <div class="address-label">Bill To</div>
            <div class="address-company">{{ currentUser?.business?.business?.name }}</div>
            <div class="address-detail">{{ currentUser?.business?.business?.address }}</div>
            <div class="address-detail" v-if="currentUser?.business?.business?.email">
              <font-awesome-icon icon="envelope" class="me-1" />
              {{ currentUser?.business?.business?.email }}
            </div>
            <div class="address-detail" v-if="currentUser?.business?.business?.phone_number">
              <font-awesome-icon icon="phone" class="me-1" />
              {{ currentUser?.business?.business?.phone_number }}
            </div>
            <div class="address-detail" v-if="currentUser?.business?.business?.phone_number2">
              <font-awesome-icon icon="phone" class="me-1" />
              {{ currentUser?.business?.business?.phone_number2 }}
            </div>
          </div>
  
          <div class="address-block">
            <div class="address-label">Invoice Details</div>
            <div class="invoice-detail-row">
              <span class="detail-key">Issue Date</span>
              <span class="detail-val">{{ dateTime(subscriptions?.subscription_date) }}</span>
            </div>
            <div class="invoice-detail-row">
              <span class="detail-key">Due Date</span>
              <span class="detail-val">{{ dateTime(subscriptions?.expiry_date) }}</span>
            </div>
            <div class="invoice-detail-row">
              <span class="detail-key">Amount Due</span>
              <span class="detail-val fw-bold">
                {{ currentUserCurrency?.symbol }}{{ $filters.formatMoney(displayAmount(subscriptions?.plan_detail?.amount, currentUserCurrency)) }}
              </span>
            </div>
          </div>
  
        </div>
  
        <div class="invoice-divider"></div>
  
        <!-- Line items -->
        <div class="items-section">
          <div class="items-header">
            <span>Description</span>
            <span>Amount</span>
          </div>
          <div class="items-body">
            <div class="item-row">
              <div class="item-desc">
                <div class="item-name">{{ planName }}</div>
                <div class="item-cycle">{{ billingCycle }}</div>
              </div>
              <div class="item-amount">
                {{ currentUserCurrency?.symbol }}{{ $filters.formatMoney(displayAmount(subscriptions?.plan_detail?.amount, currentUserCurrency)) }}
              </div>
            </div>
          </div>
        </div>
  
        <div class="invoice-divider"></div>
  
        <!-- Totals -->
        <div class="totals-section">
          <div class="total-row">
            <span class="total-key">Subtotal</span>
            <span class="total-val">
              {{ currentUserCurrency?.symbol }}{{ $filters.formatMoney(displayAmount(subscriptions?.plan_detail?.amount, currentUserCurrency)) }}
            </span>
          </div>
          <div class="total-row total-row--grand">
            <span class="total-key">Total</span>
            <span class="total-val fw-bold">
              {{ currentUserCurrency?.symbol }}{{ $filters.formatMoney(displayAmount(subscriptions?.plan_detail?.amount, currentUserCurrency)) }}
            </span>
          </div>
        </div>
  
        <!-- Footer note -->
        <div class="invoice-footer">
          <font-awesome-icon icon="check-circle" class="footer-icon" />
          Thank you for your subscription. This invoice was generated automatically.
        </div>
        
  
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted } from 'vue';
  import { useRoute } from 'vue-router';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import axios from 'axios';
  import hostName from '../../../main';
  
  export default {
    name: 'SubscriptionInvoice',
  
    mixins: [reusableMixin],
  
    setup() {
      const route         = useRoute();
      const loading       = ref(false);
      const subscriptions = ref(null);
      const details = ref(null);
      const url = ref(hostName);

  
      // ── Computed helpers ─────────────────────────────────────────────
      const planName = computed(() => {
        const sub = subscriptions.value;
        if (!sub) return '—';
        if (sub.user?.trial_subscription === '14 days trial') return '14 Days Trial';
        return sub.plan_detail?.plan?.name +' Plan Subscription' ?? '—';
      });
  
      const billingCycle = computed(() => {
        const type = subscriptions.value?.plan_detail?.type;
        const map = { Monthly: 'Monthly billing', Quarterly: 'Quarterly billing (3 months)', 'Half Year': 'Semi-annual billing (6 months)', Yearly: 'Annual billing' };
        return map[type] ?? type ?? '';
      });
  
      // ── Print ─────────────────────────────────────────────────────────
      const printInvoice = () => {
        const content = document.getElementById('invoice-content');
        if (!content) return;
        const win = window.open('', 'PRINT', 'height=700,width=900');
        win.document.write(`
          <html>
            <head>
              <title>Invoice ${subscriptions.value?.invoice_number ?? ''}</title>
              <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: 'Segoe UI', sans-serif; color: #1a2b45; padding: 40px; font-size: 13px; }
                .invoice-head { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; }
                .brand-name { font-size:18px; font-weight:700; }
                .invoice-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#7a90b0; }
                .invoice-number { font-size:20px; font-weight:700; margin:4px 0; }
                .invoice-divider { border:none; border-top:1px solid #e4eaf2; margin:20px 0; }
                .invoice-addresses { display:flex; gap:40px; margin-bottom:20px; }
                .address-label { font-size:11px; font-weight:700; text-transform:uppercase; color:#7a90b0; margin-bottom:8px; }
                .address-company { font-weight:700; font-size:14px; margin-bottom:4px; }
                .address-detail { font-size:13px; color:#4a5e78; margin-bottom:3px; }
                .invoice-detail-row { display:flex; justify-content:space-between; gap:20px; margin-bottom:5px; }
                .detail-key { color:#7a90b0; }
                .detail-val { font-weight:600; }
                .items-header { display:flex; justify-content:space-between; font-size:11px; font-weight:700; text-transform:uppercase; color:#7a90b0; padding:10px 0; }
                .item-row { display:flex; justify-content:space-between; align-items:flex-start; padding:16px 0; }
                .item-name { font-weight:600; }
                .item-cycle { font-size:12px; color:#7a90b0; margin-top:3px; }
                .item-amount { font-weight:600; }
                .totals-section { display:flex; flex-direction:column; align-items:flex-end; gap:8px; }
                .total-row { display:flex; gap:40px; justify-content:flex-end; font-size:13px; }
                .total-row--grand { font-size:15px; font-weight:700; padding-top:8px; border-top:1px solid #e4eaf2; margin-top:4px; }
                .invoice-footer { text-align:center; color:#7a90b0; font-size:12px; margin-top:40px; padding-top:20px; border-top:1px solid #e4eaf2; }
              </style>
            </head>
            <body>${content.innerHTML}</body>
          </html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
      };
  
      // ── Data ──────────────────────────────────────────────────────────
      const activeSubscription = () => {
        loading.value = true;
        axios.get('/subscription/user/' + route.params.id)
          .then(res => { subscriptions.value = res.data.data; })
          .catch(() => {})
          .finally(() => { loading.value = false; });
      };

      const pageData = () => {
           loading.value = true;
            axios.get("/admin/fetchProjectSetup").then((response) => {
                details.value = response.data.data;
            })
            .catch(() => {})
            .finally(() => { loading.value = false; });
        };
  
        onMounted(() => {
            activeSubscription();
            pageData();
        });
  
      return { loading, subscriptions, planName, billingCycle, printInvoice, details, url };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .invoice-page { display:flex; flex-direction:column; gap:20px; max-width:860px; margin:0 auto; }
  
  /* ── Header ──────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  
  .btn-ghost   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; text-decoration:none; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; color:#1a2b45; text-decoration:none; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover:not(:disabled) { background:#f4f7fb; }
  .btn-outline:disabled { opacity:.5; cursor:not-allowed; }
  .btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary:hover:not(:disabled) { background:#003b8e; }
  .btn-primary:disabled { opacity:.5; cursor:not-allowed; }
  
  /* ── State ───────────────────────────────────────────────────────────── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .text-muted-cell { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  
  /* ── Invoice card ────────────────────────────────────────────────────── */
  .invoice-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:48px 52px; box-shadow:0 4px 20px rgba(0,0,0,.05); }
  
  /* ── Invoice head ────────────────────────────────────────────────────── */
  .invoice-head { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:32px; }
  .brand-name   { font-size:20px; font-weight:800; color:#1a2b45; }
  .logo-img     { max-height:48px; object-fit:contain; }
  
  .invoice-meta   { text-align:right; }
  .invoice-label  { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#7a90b0; margin-bottom:4px; }
  .invoice-number { font-size:16px; font-weight:700; margin:4px 0; word-break:break-all; line-height:1.35; max-width:220px; }
  
  .invoice-status  { display:inline-flex; align-items:center; gap:6px; border-radius:6px; padding:3px 12px; font-size:12px; font-weight:600; }
  .status--paid    { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
  .status-dot      { width:6px; height:6px; border-radius:50%; background:currentColor; }
  
  /* ── Divider ─────────────────────────────────────────────────────────── */
  .invoice-divider { border:none; border-top:1px solid #e4eaf2; margin:28px 0; }
  
  /* ── Addresses ───────────────────────────────────────────────────────── */
  .invoice-addresses { display:grid; grid-template-columns:repeat(3,1fr); gap:32px; }
  .address-label   { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin-bottom:10px; }
  .address-company { font-size:14px; font-weight:700; color:#1a2b45; margin-bottom:5px; }
  .address-detail  { font-size:13px; color:#4a5e78; margin-bottom:4px; display:flex; align-items:center; }
  
  .invoice-detail-row { display:flex; justify-content:space-between; gap:16px; margin-bottom:6px; font-size:13px; }
  .detail-key { color:#7a90b0; }
  .detail-val { font-weight:600; color:#1a2b45; }
  .fw-bold    { font-weight:700; }
  .me-1       { margin-right:4px; }
  
  /* ── Line items ──────────────────────────────────────────────────────── */
  .items-header  { display:flex; justify-content:space-between; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding-bottom:12px; }
  .item-row      { display:flex; justify-content:space-between; align-items:flex-start; padding:18px 0; }
  .item-desc     { flex:1; }
  .item-name     { font-size:14px; font-weight:600; color:#1a2b45; margin-bottom:4px; }
  .item-cycle    { font-size:12px; color:#7a90b0; }
  .item-amount   { font-size:14px; font-weight:600; color:#1a2b45; }
  
  /* ── Totals ──────────────────────────────────────────────────────────── */
  .totals-section { display:flex; flex-direction:column; align-items:flex-end; gap:10px; }
  .total-row      { display:flex; align-items:center; gap:60px; font-size:13px; }
  .total-row--grand { font-size:16px; font-weight:700; color:#1a2b45; padding-top:12px; border-top:2px solid #e4eaf2; margin-top:4px; }
  .total-key      { color:#7a90b0; min-width:80px; text-align:right; }
  .total-val      { min-width:100px; text-align:right; color:#1a2b45; }
  
  /* ── Footer ──────────────────────────────────────────────────────────── */
  .invoice-footer { display:flex; align-items:center; justify-content:center; gap:8px; margin-top:48px; padding-top:20px; border-top:1px solid #f0f4f8; font-size:12px; color:#7a90b0; }
  .footer-icon    { color:#16a34a; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .invoice-card       { padding:28px 20px; }
    .invoice-addresses  { grid-template-columns:1fr; gap:20px; }
    .page-header        { flex-direction:column; }
    .page-actions       { width:100%; }
    .invoice-head       { flex-direction:column; gap:16px; }
    .invoice-meta       { text-align:left; }
  }
  
  @media print {
    .invoice-page { max-width:100%; }
    .page-header  { display:none; }
    .invoice-card { border:none; box-shadow:none; padding:0; }
  }
  </style>